/**
 * Esteira de etapas do caso (E47.F1.T04, ADR-0058 §4): Criação · Análise
 * preliminar · Mesa · Laudo. Puramente APRESENTAÇÃO/navegação — não cria estado
 * de fluxo novo. Os estados de cada etapa são derivados do `workflow_status` já
 * existente (E23.F24), com um hint da etapa ativa (`activeStage`) para
 * desambiguar `em_analise`, que cobre tanto a triagem quanto a mesa.
 */
import { CheckCircle2, Circle, CircleDot } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import type { Modalidade, WorkflowStatus } from '@/lib/cases';

export type CaseStage = 'criacao' | 'preliminar' | 'mesa' | 'laudo';
export type ActiveStage = 'preliminar' | 'mesa' | 'laudo';
/**
 * E74.F3.T10: `laudo` entrou porque a modalidade eletrônica ganhou tela própria
 * na etapa do documento. No grafotécnico a tela do laudo não desenha a esteira,
 * então o valor não existia — e sem ele a página nova teria de mentir sobre em
 * que etapa o caso está.
 */
type StageState = 'done' | 'current' | 'upcoming';

const STAGE_ORDER: readonly CaseStage[] = ['criacao', 'preliminar', 'mesa', 'laudo'];

const STAGE_LABEL: Record<CaseStage, string> = {
  criacao: ptBR.caseStepper.stageCriacao,
  preliminar: ptBR.caseStepper.stagePreliminar,
  mesa: ptBR.caseStepper.stageMesa,
  laudo: ptBR.caseStepper.stageLaudo,
};

/**
 * Índice da etapa mais avançada ALCANÇADA pelo caso, a partir do
 * `workflow_status`. Em `em_analise` o status não distingue triagem de mesa —
 * o `activeStage` (contexto da página que renderiza a esteira) resolve.
 */
function reachedIndex(status: WorkflowStatus | null | undefined, activeStage: ActiveStage): number {
  switch (status) {
    case 'rascunho':
      return 0; // criacao
    // E72.F1 — o desfecho com Relatório Preliminar é terminal na MESMA etapa: o
    // caso encerrou na triagem, com documento. O que muda é o rótulo da lista.
    case 'encerrado_triagem':
    case 'encerrado_com_relatorio':
      return 1; // terminal preliminar
    // `eletronica_concluida` (E78.F1) alcançou a mesma etapa: o laudo está
    // disponível, mas ainda não homologado — por isso não é terminal.
    case 'laudo_em_elaboracao':
    case 'eletronica_concluida':
    case 'aguardando_revisor':
    case 'devolvido_pelo_revisor':
    case 'finalizado':
      return 3; // laudo
    case 'em_analise':
    default:
      // `em_analise` não distingue triagem, mesa e laudo — quem resolve é a
      // página que desenha a esteira.
      return STAGE_ORDER.indexOf(activeStage);
  }
}

/**
 * Estado de cada etapa (done/current/upcoming). Estados terminais
 * (`encerrado_triagem`, `encerrado_com_relatorio`, `finalizado`) marcam a etapa
 * alcançada como concluída;
 * caso contrário, a etapa alcançada é a atual.
 */
export function caseStageStates(
  status: WorkflowStatus | null | undefined,
  activeStage: ActiveStage,
): Record<CaseStage, StageState> {
  const reached = reachedIndex(status, activeStage);
  const terminal =
    status === 'finalizado' ||
    status === 'encerrado_triagem' ||
    status === 'encerrado_com_relatorio';
  const entries = STAGE_ORDER.map((stage, i): [CaseStage, StageState] => {
    if (i < reached) return [stage, 'done'];
    if (i === reached) return [stage, terminal ? 'done' : 'current'];
    return [stage, 'upcoming'];
  });
  return Object.fromEntries(entries) as Record<CaseStage, StageState>;
}

const STATE_CLASS: Record<StageState, string> = {
  done: 'border-success-500 bg-success-50 text-success-700 hover:bg-success-100 cursor-pointer',
  current:
    'border-accent bg-accent-bg text-accent-700 font-semibold ring-2 ring-accent cursor-pointer',
  upcoming: 'border-border bg-bg-deep text-fg-muted opacity-70 cursor-not-allowed',
};

function StageIcon({ state }: { state: StageState }): JSX.Element {
  if (state === 'done') return <CheckCircle2 className="h-4 w-4 text-success-700" aria-hidden />;
  if (state === 'current') return <CircleDot className="h-4 w-4 text-accent" aria-hidden />;
  return <Circle className="h-4 w-4 text-fg-muted" aria-hidden />;
}

/**
 * Rota de cada etapa. Etapas ALCANÇADAS (done/current) navegam; `upcoming` fica
 * bloqueada.
 *
 * E78.F1: "Criação" abre a PREPARAÇÃO do caso (o assistente de envio), e não
 * mais `/casos/:id` — que era o placeholder "Detalhe do caso" e hoje é o
 * resolvedor de destino: mandar a etapa para lá devolveria a pessoa à etapa em
 * que ela já está, em vez de deixá-la voltar ao cadastro e aos documentos.
 */
const STAGE_ROUTE: Record<CaseStage, (caseId: string) => string> = {
  criacao: (id) => `/casos/${id}/wizard`,
  preliminar: (id) => `/casos/${id}/pre-analise`,
  mesa: (id) => `/casos/${id}/workspace`,
  laudo: (id) => `/casos/${id}/laudo`,
};

/**
 * E74.F1 (ADR-0075): a mesa da modalidade eletrônica é outra tela. A rota
 * `/casos/:id/mesa-eletronica` só ganha página na E74.F3; até lá ela cai no
 * NotFound — o que é aceitável porque, num caso eletrônico, a etapa "Mesa" só
 * fica navegável quando o fluxo já passou da pré-análise, o que o backend ainda
 * não permite nesta Fase. Opção escolhida por ser a mais simples: um mapa de
 * rota, sem estado novo no stepper.
 */
function rotaDaEtapa(stage: CaseStage, caseId: string, modalidade: Modalidade): string {
  if (stage === 'mesa' && modalidade === 'eletronica') {
    return `/casos/${caseId}/mesa-eletronica`;
  }
  return STAGE_ROUTE[stage](caseId);
}

/** Etapa navegável = já alcançada (concluída ou atual). Futuras ficam bloqueadas. */
function isNavigable(state: StageState): boolean {
  return state === 'done' || state === 'current';
}

const NODE_BASE =
  'inline-flex items-center gap-1.5 border px-[11px] py-1 font-display text-[13px] tracking-[0.03em]';

export interface CaseStageStepperProps {
  caseId: string;
  workflowStatus: WorkflowStatus | null | undefined;
  activeStage: ActiveStage;
  /** E74.F1 — omitida = grafotécnica (rotas originais). */
  modalidade?: Modalidade | null | undefined;
}

export function CaseStageStepper({
  caseId,
  workflowStatus,
  activeStage,
  modalidade,
}: CaseStageStepperProps): JSX.Element {
  const states = caseStageStates(workflowStatus, activeStage);
  const modalidadeEfetiva: Modalidade = modalidade ?? 'grafotecnica';
  return (
    <nav
      aria-label={ptBR.caseStepper.ariaLabel}
      data-testid="case-stage-stepper"
      data-modalidade={modalidadeEfetiva}
    >
      <ol className="flex flex-wrap items-center gap-2">
        {STAGE_ORDER.map((stage) => {
          const state = states[stage];
          const shared = {
            className: `${NODE_BASE} ${STATE_CLASS[state]}`,
            'data-testid': `case-stage-${stage}`,
            'data-state': state,
          };
          return (
            <li key={stage}>
              {isNavigable(state) ? (
                <Link
                  {...shared}
                  to={rotaDaEtapa(stage, caseId, modalidadeEfetiva)}
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  <StageIcon state={state} />
                  {STAGE_LABEL[stage]}
                </Link>
              ) : (
                <span {...shared} aria-disabled="true" title={ptBR.caseStepper.blockedHint}>
                  <StageIcon state={state} />
                  {STAGE_LABEL[stage]}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
